<script setup lang="ts">

import TempImageSelection from './TempImageSelection.vue'
import Dialog from 'primevue/dialog'
import DropImageModalContent
  from '@/features/search/components/drag-and-drop/DropImageModalContent.vue'
import { useImageSearch } from '@/features/search/store'
import { useToast } from 'primevue/usetoast'
import { useDevice } from '@/composables/useDevice'
import { computed, ref } from 'vue'
import { useSharedStateForComponents } from '@/features/search/store'
import type { FileUploadUploaderEvent } from 'primevue/fileupload'
import { useUploadSearchImageMutation } from '@/mutations'
import type { UploadImageResponse } from '@/types/searchItemsTypes'

const imageSearch = useImageSearch();
const toast = useToast();
const { currentModalContentSteps, setStep2ForImageFlow } = useSharedStateForComponents();

const { isDevice } = useDevice();
const deviceType = computed(() => isDevice.value);

const imageHeightBasedOnScreen = computed(() => window.screen.height * 0.5);
const imageWidthBasedOnScreen = computed(() => deviceType.value === 'mobile' ? window.screen.width * 0.8 : 560);

const base64data = computed(() => imageSearch.imageBase64Data ?? '');

const showToast = (msg: string) => {
  toast.add({ severity: "error", summary: "Info", detail: msg, life: 3000 });
};

function handlerBase64Data(base64ImageData: string) {
  imageSearch.imageBase64Data = base64ImageData;
  setStep2ForImageFlow();
}

const uploadImageMutation = useUploadSearchImageMutation();

const customBase64Uploader = async (event: FileUploadUploaderEvent) => {
  const files = Array.isArray(event.files) ? event.files : event.files ? [event.files] : [];
  const uploadedFile = files[0];

  if (!uploadedFile) {
    showToast("Не удалось получить файл изображения");
    return;
  }

  imageSearch.imageFile = uploadedFile;

  const reader = new FileReader();
  reader.readAsDataURL(uploadedFile);

  reader.onloadend = async () => {
    if (typeof reader.result !== 'string') {
      showToast("Не удалось преобразовать изображение");
      return;
    }

    imageSearch.imageBase64Data = reader.result;

    try {
      const response = await uploadImageMutation.mutateAsync({ file: uploadedFile }) as UploadImageResponse;

      if (response?.img_url) {
        imageSearch.currentImageLink = response.img_url;
      } else {
        showToast("Что-то пошло не так: ошибка ответа сервера");
      }
    } catch (error) {
      console.error('uploadImage failed', error);
      showToast("Не удалось загрузить изображение");
      return;
    }

    setStep2ForImageFlow();
  };

  reader.onerror = () => {
    showToast("Не удалось прочитать файл изображения");
  };
};


function cropImageHandler(imageCode: string) {
  imageSearch.croppedImageLink = imageCode;
}
</script>

<template>
  <Dialog
    :header="currentModalContentSteps.step1.active ? ' ' : 'Select area'"
    v-model:visible="imageSearch.isImageSearchModalOpen"
    :pt="{
        root: {
          style: {
            border: '20px',
            borderColor: 'transparent',
            background: 'white',
            borderRadius: '20px',
            maxHeight: '98%',
            maxWidth:'640px',
            width:'98%',
            height: 'fit-content',
          },
        },
        header:{
          style:{
            borderColor: 'transparent',
            background: 'white',
            padding:'20px',
            borderRadius: '20px',
          }
        },
        mask: {
          style: 'backdrop-filter: blur(2px)',
        },
        content: {
          style: {
            padding:0,
          },
        },
      }"
  >
    <DropImageModalContent
      v-if="currentModalContentSteps.step1.active"
      @set-base64-image-data="handlerBase64Data"
      @custom-upload-image="customBase64Uploader"
    />
    <div v-if="currentModalContentSteps.step2.active"
         class="select-and-upload-image-content"
    >
      <div class="select-and-upload-image-content-cropper-wrapper">
        <temp-image-selection
          :image-height="imageHeightBasedOnScreen"
          :image-width="imageWidthBasedOnScreen"
          :current-image-link="base64data"
          @set-new-image-link="cropImageHandler"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>

.select-and-upload-image-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-height: 90vh;
  background: white;
  border-radius: 20px;
  padding: 20px;
}

.select-and-upload-image-content-cropper-wrapper {
  position: relative;
}

</style>